/**
 * 🔒 A list row as the List Viewer's Compact mode reads it (piece 4; decisions
 * 0259, 0639, 1444, 1544), written once as text so a renderer that is not the
 * browser's draws the same row (decision 0242). The phone app's lists read it
 * (decision 3420): the primary column on the first line beside the status pill,
 * the secondary and date columns joined on the second line, and the value
 * column at its end.
 *
 * It formats each cell as the web's List Viewer does: a number with its unit, a
 * calendar day as the day it names, an instant on the viewer's own day, money
 * through the one money formatter, and a status by its seeded label rather than
 * its stored member. Nothing here reads the DOM.
 */
import type { ColumnDef } from './listViewer';
import { compactFigure, countWithUnit } from './listViewer';
import { formatCurrency } from '../money';
import { formatDate, type DateFormat } from '../settings/dateFormat';
import { heldDraftPill } from '../documents/statuses';
import type { StatusColorGroup, StatusResolver } from '../statusDefinitions';

/** What a cell needs to be read: the viewer's zone and date format (decision 0261), the currency, and the one status resolver. */
export interface ListCellReading {
  zone: string;
  dateFormat: DateFormat;
  currencyCode: string;
  resolveStatus: StatusResolver;
}

/** The row as the Compact mode draws it, every part already text. */
export interface CompactRowText {
  primary: string;
  secondary: string;
  status: { label: string; colorGroup: StatusColorGroup } | null;
  value: string;
}

/** T2962: the grouped-count formatter, built once. */
const GROUPED = new Intl.NumberFormat('en-US');

type Row = Readonly<Record<string, unknown>>;

/** One cell as text, by its column's type (decisions 0481, 1260, 1444). */
export function formatListCell(column: ColumnDef, value: unknown, reading: Pick<ListCellReading, 'zone' | 'dateFormat' | 'currencyCode'>): string {
  if (value === null || value === undefined) return '';
  switch (column.type) {
    case 'currency':
      return typeof value === 'number' ? formatCurrency({ amount: value, currencyCode: reading.currencyCode }) : String(value);
    case 'number': {
      if (typeof value !== 'number') return String(value);
      const figure = GROUPED.format(value);
      return column.unit ? countWithUnit(value, figure, column.unit) : figure;
    }
    // 🔒 A picture has no text (T3333): the compact line and the export read nothing for it.
    case 'image':
      return '';
    case 'date':
    case 'instant': {
      const day = value instanceof Date ? value : new Date(String(value));
      return Number.isNaN(day.getTime()) ? String(value) : formatDate(day, column.type === 'instant' ? reading.zone : 'UTC', reading.dateFormat);
    }
    default:
      return String(value);
  }
}

/** A status cell's pill: the approval's pill on a held Draft (decision 1861), the seeded label otherwise, the stored word where no row names it. */
export function statusPillOf(column: ColumnDef, row: Row, resolveStatus: StatusResolver): CompactRowText['status'] {
  const value = row[column.key];
  if (value === null || value === undefined || value === '') return null;
  const held = column.heldByKey ? heldDraftPill(String(value), String(row[column.heldByKey] ?? '')) : null;
  if (held) return { label: held.text, colorGroup: held.group };
  const resolved = resolveStatus(column.statusEntity ?? '', String(value));
  return resolved ? { label: resolved.label, colorGroup: resolved.colorGroup } : { label: String(value), colorGroup: 'inactive' };
}

/** The Compact row of one list row over the list's columns. */
export function compactRowText(columns: readonly ColumnDef[], row: Row, reading: ListCellReading): CompactRowText {
  const primary = columns.find((column) => column.role === 'primary');
  const statusColumn = columns.find((column) => column.role === 'status');
  const valueColumn = columns.find((column) => column.role === 'value');
  // 🔒 A picture and a row's marks are no words of the second line (decisions 9380, 9720).
  const secondaries = columns.filter((column) => column.type !== 'image' && column.type !== 'marks' && (column.role === 'secondary' || column.role === 'date'));
  const secondaryText = (column: ColumnDef): string => {
    if (column.type === 'status') return compactFigure(column, statusPillOf(column, row, reading.resolveStatus)?.label ?? '');
    const text = formatListCell(column, row[column.key], reading);
    return text === '' ? '' : compactFigure(column, text);
  };
  return {
    primary: primary ? formatListCell(primary, row[primary.key], reading) : '',
    secondary: secondaries.map(secondaryText).filter(Boolean).join(' · '),
    status: statusColumn ? statusPillOf(statusColumn, row, reading.resolveStatus) : null,
    value: valueColumn ? formatListCell(valueColumn, row[valueColumn.key], reading) : '',
  };
}
